import api from './api';
import secureStorage from '@/utils/secureStorage';

export const authAPI = {
  login: async (credentials) => {
    const { rememberMe, ...loginCredentials } = credentials;
    const response = await api.post('/auth/login/', loginCredentials);

    if (response.data.access) {
      secureStorage.setAccessToken(response.data.access);
      // Pass rememberMe to determine storage type (localStorage vs sessionStorage)
      secureStorage.setRefreshToken(response.data.refresh, rememberMe);

      // Store user data if present
      if (response.data.user) {
        secureStorage.setUser(response.data.user);
      }

      // Store access control permissions
      if (response.data.access_control) {
        secureStorage.setAccessControl(response.data.access_control);
      }
    }

    return response.data;
  },

  logout: async () => {
    const refreshToken = secureStorage.getRefreshToken();
    if (refreshToken) {
      try {
        await api.post(
          '/auth/logout/',
          { refresh_token: refreshToken },
          {
            skipAuthInterceptor: true, // Skip interceptor to prevent "Session expired" toast
          }
        );
      } catch {
        // Still clear local data even if the API call fails
      }
    }
    secureStorage.clearAuthData();
  },

  getCurrentUser: async () => {
    const response = await api.get('/auth/users/me/');
    return response.data;
  },

  refreshToken: async (refreshTokenValue) => {
    const response = await api.post('/auth/token/refresh/', {
      refresh: refreshTokenValue,
    });
    // Save the rotated refresh token (backend blacklists the old one)
    if (response.data.refresh) {
      secureStorage.setRefreshToken(response.data.refresh);
    }
    return response.data;
  },

  isAuthenticated: () => {
    return secureStorage.isAuthenticated();
  },

  /**
   * Check if user has a valid session (refresh token exists)
   * Use this to check if user can be auto-logged in
   */
  hasSession: () => {
    return secureStorage.hasSession();
  },

  getToken: () => {
    return secureStorage.getAccessToken();
  },

  getRefreshToken: () => {
    return secureStorage.getRefreshToken();
  },

  /**
   * Check if remember me is enabled
   */
  isRememberMeEnabled: () => {
    return secureStorage.isRememberMeEnabled();
  },

  /**
   * Fetch current user's access control permissions
   * This allows refreshing permissions without re-login
   */
  getAccessControl: async () => {
    const response = await api.get('/admin/me/access/');

    // Store access control permissions (already in correct array format)
    if (response.data && response.data.access_control) {
      secureStorage.setAccessControl(response.data.access_control);
      return response.data.access_control;
    }

    return null;
  },

  /**
   * Send verification OTP for forgot password flow
   * POST /api/v1/auth/users/verification/send/
   */
  sendVerificationOTP: async (data) => {
    const response = await api.post('/auth/users/verification/send/', data);
    return response.data;
  },

  /**
   * Verify OTP code
   * POST /api/v1/auth/users/verification/verify/
   */
  verifyOTP: async (data) => {
    const response = await api.post('/auth/users/verification/verify/', data);
    return response.data;
  },

  /**
   * Reset password after OTP verification
   * POST /api/v1/auth/forgot-password/reset/
   */
  resetPassword: async (data) => {
    const response = await api.post('/auth/forgot-password/reset/', data);
    return response.data;
  },
};
